import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon, Loader2, FileText, AlertCircle, Check, User
} from 'lucide-react';
import { formatDateToLocal } from '../../lib/dateUtils';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// SOP Modal Component
function SOPModal({ isOpen, onClose, clients, departments, employees, holidays, api, onSuccess, loading, setLoading, userType = 'admin' }) {
  const [formData, setFormData] = useState({
    client_id: '',
    department_id: '',
    day_of_week: '',
    task_title: '',
    task_subtype: 'Post', // Default subtype
    due_date_sop: '7_days_before',
    primary_assignee_id: '',
    applicable_month: ''
  });

  const daysOfWeek = [
    { value: '1', label: 'Monday' },
    { value: '2', label: 'Tuesday' },
    { value: '3', label: 'Wednesday' },
    { value: '4', label: 'Thursday' },
    { value: '5', label: 'Friday' },
    { value: '6', label: 'Saturday' },
    { value: '0', label: 'Sunday' }
  ];

  // Subtypes for Social Media Calendar
  const subtypeOptions = [
    { value: 'Post', label: 'Post' },
    { value: 'Reel', label: 'Reel' },
    { value: 'Story', label: 'Story' }
  ];

  const dueDateOptions = [
    { value: '5_days_before', label: '5 days before' },
    { value: '7_days_before', label: '7 days before' },
    { value: '15_days_before', label: '15 days before' },
    { value: '30_days_before', label: '30 days before' },
    { value: 'friday_before', label: 'Friday before' }
  ];

  // Get selected client
  const selectedClient = clients.find(c => c._id === formData.client_id);

  // Get departments for selected client only
  const clientDepartments = selectedClient?.departments || [];
  const filteredDepartments = departments.filter(d => 
    d.is_active && clientDepartments.includes(d.name)
  );

  // Get default POC for selected department from client's POCs
  const getDefaultPOC = (departmentName) => {
    if (!selectedClient || !departmentName) return '';
    const pocs = selectedClient.pocs || {};
    return pocs[departmentName] || '';
  };

  // Handle client change - reset department and assignee
  const handleClientChange = (clientId) => {
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      department_id: '',
      primary_assignee_id: ''
    }));
  };

  // Handle department change - auto-populate POC
  const handleDepartmentChange = (departmentName) => {
    const defaultPOC = getDefaultPOC(departmentName);
    setFormData(prev => ({
      ...prev,
      department_id: departmentName,
      primary_assignee_id: defaultPOC
    }));
  };

  const getMonthOptions = () => {
    const months = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`
      });
    }
    return months;
  };

  // Get all dates of a specific day of week in a given month
  const getDatesOfDayInMonth = (year, month, dayOfWeek) => {
    const dates = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Find first occurrence of the day
    let current = new Date(firstDay);
    while (current.getDay() !== dayOfWeek) {
      current.setDate(current.getDate() + 1);
    }
    
    // Collect all occurrences
    while (current <= lastDay) {
      // Only include dates that are today or in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (current >= today) {
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 7);
    }
    
    return dates;
  };

  // Check if a date is a holiday or weekend
  const isNonWorkingDay = (date) => {
    const dayOfWeek = date.getDay();
    // Weekend check
    if (dayOfWeek === 0 || dayOfWeek === 6) return true;
    // Holiday check - ensure holidays is an array
    if (!Array.isArray(holidays)) return false;
    const dateStr = formatDateToLocal(date);
    return holidays.some(h => h.date === dateStr);
  };

  // Get nearest working day before the given date
  const getNearestWorkingDayBefore = (date) => {
    let current = new Date(date);
    current.setDate(current.getDate() - 1); // Start from day before
    
    while (isNonWorkingDay(current)) {
      current.setDate(current.getDate() - 1);
    }
    
    return current;
  };

  // Calculate due date based on SOP rule
  const calculateDueDate = (useDate, sopRule) => {
    let dueDate = new Date(useDate);
    
    switch (sopRule) {
      case '5_days_before':
        dueDate.setDate(dueDate.getDate() - 5);
        break;
      case '7_days_before':
        dueDate.setDate(dueDate.getDate() - 7);
        break;
      case '15_days_before':
        dueDate.setDate(dueDate.getDate() - 15);
        break;
      case '30_days_before':
        dueDate.setDate(dueDate.getDate() - 30);
        break;
      case 'friday_before':
        // Find the Friday before (or on) the use date
        while (dueDate.getDay() !== 5) {
          dueDate.setDate(dueDate.getDate() - 1);
        }
        // If we landed on the same date, go back a week
        if (dueDate.getTime() === useDate.getTime()) {
          dueDate.setDate(dueDate.getDate() - 7);
        }
        break;
      default:
        dueDate.setDate(dueDate.getDate() - 7);
    }
    
    // Adjust to nearest working day if it falls on a weekend or holiday
    if (isNonWorkingDay(dueDate)) {
      dueDate = getNearestWorkingDayBefore(dueDate);
    }
    
    return dueDate;
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.department_id) {
      toast.error('Please select a department');
      return;
    }
    if (!formData.primary_assignee_id) {
      toast.error('Please select a primary assignee');
      return;
    }
    if (!formData.day_of_week) {
      toast.error('Please select a day of the week');
      return;
    }
    if (!formData.task_title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    if (!formData.applicable_month) {
      toast.error('Please select an applicable month');
      return;
    }

    setLoading(true);
    try {
      // Parse selected month
      const [year, month] = formData.applicable_month.split('-').map(Number);
      const dayOfWeek = parseInt(formData.day_of_week);
      
      // Get all dates of the selected day in the month
      const useDates = getDatesOfDayInMonth(year, month - 1, dayOfWeek);
      
      if (useDates.length === 0) {
        toast.error('No applicable dates found in the selected month');
        setLoading(false);
        return;
      }

      // Create tasks for each date
      let createdCount = 0;
      let errorCount = 0;

      for (const useDate of useDates) {
        const dueDate = calculateDueDate(useDate, formData.due_date_sop);
        
        const taskData = {
          title: formData.task_title,
          client_id: formData.client_id,
          task_type: 'Social Media Calendar', // Fixed to Social Media Calendar
          task_subtype: formData.task_subtype, // Use selected subtype from form
          use_date: formatDateToLocal(useDate),
          due_date: formatDateToLocal(dueDate),
          due_date_sop: formData.due_date_sop, // Persist SOP rule for future drag/edit operations
          status: 'New',
          priority: 'Medium', // Default priority for SOP tasks
          primary_assignee_id: formData.primary_assignee_id || null,
          department: formData.department_id || 'Social Media', // department_id now contains dept name
          description: `SOP Task - ${daysOfWeek.find(d => d.value === formData.day_of_week)?.label}s - ${formData.task_subtype}`,
          sop_generated: true
        };

        try {
          // Use correct API endpoint based on user type
          const endpoint = userType === 'admin' ? '/api/admin/tasks' : '/api/employee/tasks';
          await api.post(endpoint, taskData);
          createdCount++;
        } catch (error) {
          console.error('Failed to create task:', error);
          errorCount++;
        }
      }

      if (createdCount > 0) {
        toast.success(`Successfully created ${createdCount} tasks`);
        if (errorCount > 0) {
          toast.warning(`${errorCount} tasks failed to create`);
        }
        onSuccess();
      } else {
        toast.error('Failed to create any tasks');
      }
    } catch (error) {
      console.error('SOP creation error:', error);
      toast.error('Failed to create SOP tasks');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      department_id: '',
      day_of_week: '',
      task_title: '',
      task_subtype: 'Post', // Reset to default
      due_date_sop: '7_days_before',
      primary_assignee_id: '',
      applicable_month: ''
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Preview dates calculation
  const getPreviewDates = () => {
    if (!formData.day_of_week || !formData.applicable_month) return [];
    const [year, month] = formData.applicable_month.split('-').map(Number);
    const dayOfWeek = parseInt(formData.day_of_week);
    return getDatesOfDayInMonth(year, month - 1, dayOfWeek).map(useDate => ({
      useDate,
      dueDate: calculateDueDate(useDate, formData.due_date_sop)
    }));
  };

  const previewDates = getPreviewDates();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0 bg-white dark:bg-[#1a1a1a]">
        <VisuallyHidden>
          <DialogTitle>Create SOP</DialogTitle>
        </VisuallyHidden>
        {/* Header */}
        <div className="bg-pink-50 dark:bg-transparent border-b border-white/40 p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#1a1a2e]">Create SOP</h2>
              <p className="text-sm text-neutral-500">Generate recurring social media tasks automatically</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Form Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Client */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Client <span className="text-red-500">*</span>
              </label>
              <Select value={formData.client_id} onValueChange={handleClientChange}>
                <SelectTrigger className="bg-white border-white/40">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client._id} value={client._id}>
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: client.color || '#9CA3AF' }}
                        />
                        {client.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department - Only show departments for selected client */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Department <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.department_id || ''} 
                onValueChange={handleDepartmentChange}
                disabled={!formData.client_id}
              >
                <SelectTrigger className={`bg-white border-white/40 ${!formData.client_id ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder={formData.client_id ? "Select department..." : "Select client first..."} />
                </SelectTrigger>
                <SelectContent>
                  {filteredDepartments.length === 0 ? (
                    <SelectItem value="_no_deps" disabled>No departments assigned to this client</SelectItem>
                  ) : (
                    filteredDepartments.map(dept => (
                      <SelectItem key={dept._id} value={dept.name}>{dept.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formData.client_id && filteredDepartments.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No departments assigned to this client. Please add departments in client settings.</p>
              )}
            </div>

            {/* Day of Week */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Day of the Week <span className="text-red-500">*</span>
              </label>
              <Select value={formData.day_of_week} onValueChange={(v) => setFormData(prev => ({ ...prev, day_of_week: v }))}>
                <SelectTrigger className="bg-white border-white/40">
                  <SelectValue placeholder="Select day..." />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map(day => (
                    <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Task Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Task Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.task_title}
                onChange={(e) => setFormData(prev => ({ ...prev, task_title: e.target.value }))}
                placeholder="e.g., Weekly Instagram Post"
                className="bg-white border-white/40"
              />
            </div>

            {/* Task Subtype */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Content Type <span className="text-red-500">*</span>
              </label>
              <Select value={formData.task_subtype} onValueChange={(v) => setFormData(prev => ({ ...prev, task_subtype: v }))}>
                <SelectTrigger className="bg-white border-white/40">
                  <SelectValue placeholder="Select content type..." />
                </SelectTrigger>
                <SelectContent>
                  {subtypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date SOP */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Due Date SOP
              </label>
              <Select value={formData.due_date_sop} onValueChange={(v) => setFormData(prev => ({ ...prev, due_date_sop: v }))}>
                <SelectTrigger className="bg-white border-white/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dueDateOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">Auto-adjusted to nearest working day</p>
            </div>

            {/* Primary Assignee - Mandatory, auto-populated from client POC */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Primary Assignee <span className="text-red-500">*</span>
              </label>
              <Select 
                value={formData.primary_assignee_id || ''} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, primary_assignee_id: v }))}
                disabled={!formData.department_id}
              >
                <SelectTrigger className={`bg-white border-white/40 ${!formData.department_id ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder={formData.department_id ? "Select assignee..." : "Select department first..."} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp._id} value={emp._id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-neutral-400" />
                        {emp.personal?.full_name || emp.work_email || 'Unknown'}
                        {getDefaultPOC(formData.department_id) === emp._id && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Default POC</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.primary_assignee_id && getDefaultPOC(formData.department_id) === formData.primary_assignee_id && (
                <p className="text-xs text-green-600 mt-1">Auto-populated from client&apos;s default POC for this department</p>
              )}
            </div>

            {/* Applicable Month */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Applicable Month <span className="text-red-500">*</span>
              </label>
              <Select value={formData.applicable_month} onValueChange={(v) => setFormData(prev => ({ ...prev, applicable_month: v }))}>
                <SelectTrigger className="bg-white border-white/40">
                  <SelectValue placeholder="Select month..." />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(month => (
                    <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">Tasks will be created for upcoming {daysOfWeek.find(d => d.value === formData.day_of_week)?.label || 'days'}s only</p>
            </div>
          </div>

          {/* Preview Section */}
          {previewDates.length > 0 && (
            <div className="bg-white/40 rounded-xl border border-white/40 p-4">
              <h4 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-pink-500" />
                Preview: {previewDates.length} tasks will be created
              </h4>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {previewDates.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-white rounded-xl px-3 py-2 border border-white/30">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                      <span className="font-medium text-[#1a1a2e]">
                        {item.useDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-neutral-500">
                      <span className="text-xs">Due:</span>
                      <span className="text-xs font-medium text-purple-600">
                        {item.dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">How it works</p>
              <ul className="mt-1 space-y-1 text-blue-600">
                <li>• Tasks are created for each selected day of the week in the chosen month</li>
                <li>• Due dates are automatically adjusted to the nearest working day</li>
                <li>• Weekends and holidays are excluded from due date calculations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white/40 border-t border-white/40 px-6 py-4 flex items-center justify-between">
          <p className="text-xs text-neutral-500">
            <span className="text-red-500">*</span> Required fields
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !formData.client_id || !formData.day_of_week || !formData.task_title || !formData.applicable_month}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create {previewDates.length || 0} Tasks
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SOPModal;
